import { getApps, initializeApp } from "firebase/app";
import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import api from "lib/axios";
import { isFirebaseConfigured } from "services/firebase/firebaseClient";
import { toastInfo } from "lib/toast";

/**
 * Web push (FCM) registration.
 *
 * Web push REQUIRES a VAPID key. If REACT_APP_FIREBASE_VAPID_KEY is not set this
 * is a no-op — instructor/admin web clients still receive realtime messages via
 * the Firestore listener while the chat is open; push is only needed when the
 * chat is NOT currently active. See README/notes for VAPID setup.
 */
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
const DEVICE_ID_KEY = "web_push_device_id";

const isProd = process.env.NODE_ENV === "production";

let registered = false;
let registering: Promise<boolean> | null = null;
let warnedMissingVapid = false;

/**
 * Course id of the chat the user is currently viewing (or null). Used to suppress
 * foreground notifications for the chat that is already open. The backend already
 * excludes users it knows are active via presence, but this guards the brief
 * window before/after a heartbeat and any same-tab race.
 */
let activeChatCourseId: string | null = null;

export type WebPushDiagnostics = {
  permission: NotificationPermission | "unsupported";
  vapidKeyPresent: boolean;
  serviceWorkerRegistered: boolean;
  tokenGenerated: boolean;
  backendRegistered: boolean;
  activeChatCourseId: string | null;
  lastError: string | null;
};

const diagnostics: WebPushDiagnostics = {
  permission: typeof Notification !== "undefined" ? Notification.permission : "unsupported",
  vapidKeyPresent: Boolean(VAPID_KEY),
  serviceWorkerRegistered: false,
  tokenGenerated: false,
  backendRegistered: false,
  activeChatCourseId: null,
  lastError: null,
};

function devLog(...args: unknown[]): void {
  if (isProd) return;
  // eslint-disable-next-line no-console
  console.log("[webPush]", ...args);
}

function setError(message: string): void {
  diagnostics.lastError = message;
  if (!isProd) {
    // eslint-disable-next-line no-console
    console.warn("[webPush]", message);
  }
}

/** A stable per-browser id so the same browser never spawns duplicate device rows. */
function getDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const generated =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const id = `web:${generated}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    return `web:${Math.random().toString(36).slice(2)}`;
  }
}

function firebaseApp() {
  if (getApps().length > 0) return getApps()[0]!;
  return initializeApp({
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
  });
}

export function isWebPushConfigured(): boolean {
  return Boolean(isFirebaseConfigured() && VAPID_KEY);
}

/** Records which course chat the user is currently viewing (null = none). */
export function setActiveChatCourseId(courseId: string | number | null): void {
  activeChatCourseId = courseId == null || courseId === "" ? null : String(courseId);
  diagnostics.activeChatCourseId = activeChatCourseId;
}

/** Dev-only snapshot of the web push state for the diagnostics panel/console. */
export function getWebPushDiagnostics(): WebPushDiagnostics {
  diagnostics.permission = typeof Notification !== "undefined" ? Notification.permission : "unsupported";
  diagnostics.vapidKeyPresent = Boolean(VAPID_KEY);
  return { ...diagnostics };
}

/**
 * Request permission (if needed), generate a web FCM token and register it with
 * the backend. Safe to call repeatedly: it only does work once per page load.
 */
export async function registerWebPush(): Promise<boolean> {
  if (registered) return false;
  if (registering) return registering;

  registering = doRegister().finally(() => {
    registering = null;
  });
  return registering;
}

async function doRegister(): Promise<boolean> {
  if (!VAPID_KEY) {
    // Web push cannot work without a VAPID key. Surface a clear dev warning so
    // this isn't silently broken (Firebase Console → Cloud Messaging → Web Push
    // certificates → "Key pair"; set REACT_APP_FIREBASE_VAPID_KEY in front/.env).
    if (!isProd && !warnedMissingVapid) {
      warnedMissingVapid = true;
      setError(
        "Firebase web push VAPID key is missing. Set REACT_APP_FIREBASE_VAPID_KEY in front/.env and restart the dev server. Web push notifications are disabled until configured."
      );
    }
    return false;
  }
  if (!isWebPushConfigured()) {
    setError("Firebase config incomplete; web push disabled.");
    return false;
  }
  if (typeof window === "undefined" || !("Notification" in window) || !("serviceWorker" in navigator)) {
    setError("This browser does not support web push (Notification/serviceWorker missing).");
    return false;
  }

  try {
    if (!(await isSupported())) {
      setError("Firebase messaging is not supported in this browser.");
      return false;
    }

    const permission = await Notification.requestPermission();
    diagnostics.permission = permission;
    devLog("notification permission:", permission);
    if (permission !== "granted") {
      setError(`Notification permission not granted (${permission}).`);
      return false;
    }

    const swReg = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    diagnostics.serviceWorkerRegistered = true;
    devLog("service worker registered:", true);

    const messaging = getMessaging(firebaseApp());

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
    if (!token) {
      setError("getToken returned an empty token.");
      return false;
    }
    diagnostics.tokenGenerated = true;
    devLog("web FCM token generated:", true, isProd ? "" : `(${token.slice(0, 12)}…)`);

    try {
      await api.post(
        "/common/firebase/fcm-token",
        { token, platform: "web", provider: "fcm", device_id: getDeviceId() },
        { meta: { toastSuccess: false, toastError: false } as never }
      );
      diagnostics.backendRegistered = true;
      devLog("backend registration: success");
    } catch (err) {
      diagnostics.backendRegistered = false;
      setError(`Backend token registration failed: ${(err as Error)?.message ?? "unknown error"}`);
      // Keep the foreground listener attached even if backend write failed; a later
      // app-start re-register will retry.
    }

    // Foreground messages arrive only when the tab is focused. If the user is on
    // the chat itself, presence marks them active and the backend won't push — so
    // a foreground message means they're elsewhere: show a visible notification.
    onMessage(messaging, (payload) => {
      const title = payload.notification?.title ?? "رسالة جديدة";
      const body = payload.notification?.body ?? "";
      const courseId = payload.data?.course_id;

      // Don't notify for the chat the user is actively viewing.
      if (courseId && activeChatCourseId && String(courseId) === activeChatCourseId) {
        devLog("foreground message suppressed (viewing same chat)", courseId);
        return;
      }

      try {
        if (Notification.permission === "granted") {
          void swReg.showNotification(title, {
            body,
            icon: "/favicon.png",
            data: payload.data ?? {},
          });
          return;
        }
        throw new Error("notification not permitted");
      } catch {
        // Fallback: the browser blocked the Notification surface in the foreground,
        // so show an in-app toast instead of silently dropping the message.
        toastInfo(body ? `${title} — ${body}` : title);
      }
    });

    registered = true;
    return true;
  } catch (err) {
    setError(`registerWebPush failed: ${(err as Error)?.message ?? "unknown error"}`);
    return false;
  }
}

// Expose a dev-only console helper so support can run `__webPushDiagnostics()`
// in DevTools without shipping a UI or leaking token values in production.
if (!isProd && typeof window !== "undefined") {
  (window as unknown as Record<string, unknown>).__webPushDiagnostics = getWebPushDiagnostics;
}
